import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import type { Deal, DealStage, TeamMember } from "../../lib/types";
import { getTransitionConfig } from "./transitionConfig";
import { STAGE_REQUIREMENTS } from "./stageRequirements";
import { notifyChadOnBuildApproval } from "../../lib/notifyChadOnBuildApproval";
import { notifyHomeownerPaymentDue } from "../../lib/notifyHomeownerPaymentDue";
import { notifyShannonOnSupplementRelease } from "../../lib/notifyShannonOnSupplementRelease";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { heicToJpeg } from "../../lib/heicToJpeg";
import SupplementEntryWarning from "../shared/SupplementEntryWarning";
import { choiceIsComplete, crossesBuildSubmission, isPlanGateError } from "../../lib/buildPaymentPlan";
import BuildPaymentPlanPicker, { planNeeded, useBuildPaymentState, usePlanChoice } from "./BuildPaymentPlanPicker";
import { passToNotifyPlan, submitWithPlan, type GatePass } from "./BuildPaymentPlanModal";

/**
 * TransitionButton — context-aware stage transition with inline form.
 *
 * Shows a labeled button for the current stage's transition.
 * On click, expands an inline form with the required fields.
 * Supports partial saves (data persists, deal stays at current stage).
 * When all required fields are met, "Save & advance" transitions the deal.
 *
 * Props:
 *   deal - the deal record
 *   onComplete - callback after transition or partial save
 *   compact - smaller button for pipeline cards (default false)
 *   members - team roster for RS select dropdowns
 */

interface Props {
  deal: Deal;
  onComplete?: () => void;
  compact?: boolean;
  members?: Record<string, TeamMember> | Record<string, { full_name: string }>;
}

const EXEC_ROLES = ["admin", "sales_director", "asm"];

export default function TransitionButton({ deal, onComplete, compact = false, members }: Props) {
  const { role, member } = useAuth();
  const config = getTransitionConfig(deal.stage);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Record<string, string | boolean>>({});
  // Files picked in "upload" fields, staged until Save actually stores them.
  // Jul 15, 2026: this used to be a fake upload — the file input flipped the
  // boolean dealColumn and DISCARDED the file, so e.g. Shannon's revised
  // estimate never reached deal_documents and the invoice email went out
  // without it. Now the file is uploaded to deal-documents storage + a
  // deal_documents row (file_type from the field's docType) before the
  // boolean is written.
  const [uploadFiles, setUploadFiles] = useState<Record<string, File>>({});

  // Submitting the build (Oct 21, 2026): when the first payment isn't captured,
  // the form asks how it will be collected and records it before the stage
  // moves. The database refuses the move without it (build_payment_plans).
  const submitsBuild = !!config && crossesBuildSubmission(deal.stage, config.nextStage);
  const pay = useBuildPaymentState(deal.id, open && submitsBuild);
  const [planChoice, setPlanChoice] = usePlanChoice(pay.snap);
  const [forcePlan, setForcePlan] = useState(false);
  const [planErr, setPlanErr] = useState<string | null>(null);
  const planOk = !submitsBuild || (!!pay.snap && (!planNeeded(pay.snap, forcePlan) || choiceIsComplete(planChoice)));

  // Initialize form data from deal
  useEffect(() => {
    if (!config) return;
    const initial: Record<string, string | boolean> = {};
    config.fields.forEach((f) => {
      const val = (deal as unknown as Record<string, unknown>)[f.dealColumn];
      if (f.type === "check") {
        initial[f.key] = !!val;
      } else if (f.type === "currency" || f.type === "number") {
        initial[f.key] = val != null && Number(val) > 0 ? String(val) : "";
      } else {
        initial[f.key] = val != null ? String(val) : "";
      }
    });
    setFormData(initial);
  }, [deal, config]);

  if (!config) return null;

  // Compute field status
  const fieldStatus = config.fields.map((f) => {
    if (f.type === "check") {
      const val = (deal as unknown as Record<string, unknown>)[f.dealColumn];
      // Special-case the balance_zero gate: post_build_balance_remaining defaults
      // to "0.00" in the DB, and !!"0.00" is truthy — which made the
      // collecting/delinquent → register_oc_warranty transition trivially passable
      // before any money was actually collected. Require BOTH zero balance AND
      // evidence that some payment has actually been received. (May 7, 2026)
      if (f.checkFn === "balance_zero") {
        const d = deal as unknown as Record<string, unknown>;
        const balanceN = Number((d.post_build_balance_remaining ?? 0) as number | string);
        const totalReceived =
          Number((d.pre_build_amount_received ?? 0) as number | string) +
          Number((d.post_build_amount_received ?? 0) as number | string) +
          Number((d.final_payment_paid ?? 0) as number | string);
        return { ...f, met: balanceN <= 0 && totalReceived > 0 };
      }
      return { ...f, met: !!val };
    }
    const formVal = formData[f.key];
    const dealVal = (deal as unknown as Record<string, unknown>)[f.dealColumn];
    const hasDealVal = dealVal != null && dealVal !== "" && dealVal !== false && (typeof dealVal !== "number" || dealVal > 0);
    const hasFormVal = formVal != null && formVal !== "" && formVal !== false;
    return { ...f, met: hasDealVal || hasFormVal };
  });

  const requiredFields = fieldStatus.filter((f) => f.required);
  // Hard gates to submit a build (Oct 22, 2026): the stage requirements for
  // build_submitted — signed agreement, homeowner color authorization, scope
  // on file, scope entered — shown as a checklist here and enforced again by
  // the database (deals_gate_build_documents). Only the build submission
  // reads them from this button; every other transition keeps its own fields.
  const gateItems = submitsBuild
    ? (STAGE_REQUIREMENTS[config.nextStage as DealStage] ?? []).map((r) => ({ key: r.key, label: r.label, required: r.required, met: r.check(deal) }))
    : [];
  const requiredGates = gateItems.filter((g) => g.required);
  const gatesMet = requiredGates.every((g) => g.met);
  const totalRequired = requiredFields.length + requiredGates.length;
  const completedRequired = requiredFields.filter((f) => f.met).length + requiredGates.filter((g) => g.met).length;
  const canAdvance = totalRequired === 0 || (requiredFields.every((f) => f.met) && gatesMet);
  const isExec = !!role && EXEC_ROLES.includes(role);
  // The exec Override skips missing FIELDS, never a hard gate — the database
  // would refuse that write anyway.
  const canOverride = isExec && gatesMet;

  // Build RS options dynamically if needed
  const rsFields = config.fields.filter((f) => f.dealColumn === "assigned_rs_id");
  if (rsFields.length > 0 && members) {
    rsFields.forEach((f) => {
      f.options = Object.entries(members)
        .filter(([, m]) => {
          const tm = m as Partial<TeamMember>;
          if (tm.is_active === false) return false;
          if (tm.role && (tm.role === "roof_specialist" || tm.role === "roof_team_manager" || tm.role === "roof_director" || tm.role === "director_of_operations")) return true;
          return !tm.role; // if no role info, include (simple members object)
        })
        .sort(([, a], [, b]) => a.full_name.localeCompare(b.full_name))
        .map(([id, m]) => ({ value: id, label: m.full_name }));
    });
  }

  async function handleSave(advance: boolean) {
    setSaving(true);
    setPlanErr(null);
    let pass: GatePass | null = null;
    try {
      // 1) Store any staged upload files FIRST — storage upload + deal_documents
      //    row — so the boolean flag is only ever set when the file is really
      //    on record. Mirrors FinanceDashboard.handleUploadAndAdvance (HEIC
      //    conversion, one retry, contentType). A failed upload aborts the save.
      const stagedKeys = Object.keys(uploadFiles);
      if (stagedKeys.length > 0) {
        await supabase.auth.refreshSession();
        for (const key of stagedKeys) {
          const field = config!.fields.find((f) => f.key === key);
          if (!field) continue;
          const upFile = await heicToJpeg(uploadFiles[key]);
          const safeName = upFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const path = `${deal.id}/${Date.now()}-${safeName}`;

          let uploadErrMsg: string | null = null;
          for (let attempt = 0; attempt < 2; attempt++) {
            const { error: upErr } = await supabase.storage
              .from("deal-documents")
              .upload(path, upFile, {
                contentType: upFile.type || "application/octet-stream",
              });
            if (!upErr) { uploadErrMsg = null; break; }
            uploadErrMsg = upErr.message;
            if (attempt === 0) await new Promise((res) => setTimeout(res, 1000));
          }
          if (uploadErrMsg) throw new Error(`"${field.label}" upload failed: ${uploadErrMsg}`);

          const { error: docErr } = await supabase.from("deal_documents").insert({
            deal_id: deal.id,
            file_name: upFile.name,
            file_type: field.docType ?? "other",
            storage_path: path,
            file_size: upFile.size,
            mime_type: upFile.type,
            uploaded_by: member?.id ?? null,
          });
          if (docErr) throw new Error(`"${field.label}" document record failed: ${docErr.message}`);

          // Stored for real — drop from staging so a retry/partial-save later
          // doesn't upload a duplicate.
          setUploadFiles((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }
      }

      // Build update payload from form data
      const update: Record<string, unknown> = {};
      config!.fields.forEach((f) => {
        if (f.type === "check") return; // checks are auto-evaluated, not user-set
        const val = formData[f.key];
        if (val === undefined || val === "") return;
        if (f.type === "currency" || f.type === "number") {
          update[f.dealColumn] = Number(val) || null;
        } else if (f.type === "upload") {
          if (val === "true" || val === true) update[f.dealColumn] = true;
        } else {
          update[f.dealColumn] = val || null;
        }
      });

      // Retail jobs skip the supplements lifecycle — a build-complete on a
      // retail deal goes straight to collecting instead of ready_for_supps.
      // (The track_stage_change DB trigger enforces this on every write path;
      // this keeps the client's local update, activity log, and notification
      // in agreement so the UI doesn't briefly claim "supps" for a retail deal.)
      const retailSkipsSupps = config!.nextStage === "ready_for_supps" && deal.is_retail === true;
      const targetStage = retailSkipsSupps ? "collecting" : config!.nextStage;

      if (advance) {
        update.stage = targetStage;
        if (config!.nextPhase) update.phase = config!.nextPhase;
        update.days_in_stage = 0;

        // Apply any auto-stamped columns (e.g. claim_called_in_date on leads → claims).
        // These overwrite anything the form put in the same column, by design.
        if (config!.autoStamps) {
          const todayIso = new Date().toISOString();
          const todayDate = todayIso.slice(0, 10); // YYYY-MM-DD
          for (const [col, mode] of Object.entries(config!.autoStamps)) {
            update[col] = mode === "today" ? todayDate : todayIso;
          }
        }
      }

      if (Object.keys(update).length > 0) {
        const doUpdate = async () => {
          const { error } = await supabase.from("deals").update(update).eq("id", deal.id);
          if (error) throw error;
        };
        if (advance && submitsBuild) {
          if (!pay.snap) throw new Error("Still loading the payments. Try again in a moment.");
          try {
            pass = await submitWithPlan({
              req: { dealId: deal.id, fromStage: deal.stage, toStage: targetStage, source: "submit_build" },
              snap: pay.snap, choice: planChoice, forcePlan, actorId: member?.id, doUpdate,
            });
          } catch (e) {
            if (isPlanGateError(e) && !forcePlan) {
              setForcePlan(true);
              setPlanErr("The server doesn't count the first payment as in yet. Pick how it will be collected, then save again.");
              return;
            }
            throw e;
          }
        } else {
          await doUpdate();
        }
      }

      // Log activity
      if (advance) {
        supabase.from("activity_log").insert({
          deal_id: deal.id,
          activity_type: "stage_change",
          title: advance ? config!.buttonLabel : "Partial save",
          description: advance
            ? `Advanced from ${deal.stage} to ${targetStage}`
            : `Saved partial progress (${completedRequired}/${totalRequired} items)`,
          metadata: {
            from_stage: deal.stage,
            to_stage: advance ? targetStage : deal.stage,
            partial: !advance,
            by: member?.full_name ?? null,
          },
        }).then(() => {});

        // Text Chad when a deal lands in his build-approval queue.
        // Fire-and-forget; helper swallows errors so a Twilio hiccup never
        // blocks the transition or shows an alert.
        if (config!.nextStage === "build_submitted" && deal.stage !== "build_submitted") {
          void notifyChadOnBuildApproval(deal, { actorId: member?.id, plan: passToNotifyPlan(pass) });
          // Same moment, text the HO their portal pay link with the live
          // first-payment amount (if they didn't pay at install meeting in
          // person, this catches them). Greg's "99/100 pay through portal"
          // path. Helper no-ops gracefully if no phone or no balance owed.
          void notifyHomeownerPaymentDue(deal);
        }
        // Notify Shannon when build completes and deal enters supplements.
        // Same fire-and-forget pattern — Shannon picks up the deal in her
        // supplements queue automatically, but the in-app notification +
        // Team-line SMS makes it impossible to miss.
        // Retail deals route to collecting (not supps), so this supps-specific
        // "new supps to start" alert only fires when we actually entered supps.
        if (targetStage === "ready_for_supps" && deal.stage !== "ready_for_supps") {
          void notifyShannonOnSupplementRelease(deal);
        }
      }

      setOpen(false);
      onComplete?.();
    } catch (err: unknown) {
      alert("Save failed: " + formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  function updateField(key: string, value: string | boolean) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  // ── Collapsed button ──
  if (!open) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => setOpen(true)}
          className={
            compact
              ? "w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition text-left " +
                (canAdvance
                  ? "bg-emerald-50 border-emerald-300 hover:bg-emerald-100"
                  : "bg-white border-slate-200 hover:bg-slate-50")
              : "w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition text-left " +
                (canAdvance
                  ? "bg-emerald-50 border-emerald-300 hover:bg-emerald-100"
                  : "bg-white border-slate-200 hover:bg-slate-50")
          }
        >
          <div className={
            "flex items-center justify-center rounded-full shrink-0 " +
            (compact ? "w-6 h-6 text-[11px]" : "w-8 h-8 text-[13px]") + " " +
            (canAdvance ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500")
          }>
            {canAdvance ? "✓" : "→"}
          </div>
          <div className="min-w-0 flex-1">
            <div className={
              (compact ? "text-[12px]" : "text-[13px]") +
              " font-semibold " +
              (canAdvance ? "text-emerald-700" : "text-slate-800")
            }>
              {config.buttonLabel}
            </div>
            {totalRequired > 0 && (
              <div className={(compact ? "text-[10px]" : "text-[11px]") + " text-slate-500 mt-0.5"}>
                {completedRequired}/{totalRequired} items ready
              </div>
            )}
          </div>
          {totalRequired > 0 && (
            <div className={"flex gap-0.5 shrink-0"}>
              {[...requiredGates, ...requiredFields].map((f, i) => (
                <div key={i} className={
                  "rounded-full " +
                  (compact ? "w-2 h-2" : "w-2.5 h-2.5") + " " +
                  (f.met ? "bg-emerald-500" : "bg-slate-300")
                } />
              ))}
            </div>
          )}
          <span className="text-slate-400 text-[16px] shrink-0">›</span>
        </button>
      </div>
    );
  }

  // ── Expanded inline form ──
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[13px] font-semibold text-slate-800">{config.buttonLabel}</div>
          {config.subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{config.subtitle}</div>}
        </div>
        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-[14px] px-1">✕</button>
      </div>

      {/* Progress bar */}
      {totalRequired > 0 && (
        <div className="px-4 pt-2">
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={"h-full rounded-full transition-all duration-300 " + (canAdvance ? "bg-emerald-500" : "bg-amber-400")}
              style={{ width: `${totalRequired > 0 ? (completedRequired / totalRequired) * 100 : 0}%` }}
            />
          </div>
          <div className="text-[10px] text-slate-400 text-right mt-1">{completedRequired}/{totalRequired} complete</div>
        </div>
      )}

      {/* Fields */}
      <div className="px-4 py-3 space-y-3">
        {gateItems.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Before this build can be submitted</div>
            {gateItems.map((g) => (
              <div key={g.key} className={"flex items-start gap-2 text-[12px] " + (g.met ? "text-slate-700" : "text-rose-700")}>
                <span className={
                  "mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 " +
                  (g.met ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600")
                }>
                  {g.met ? "✓" : "!"}
                </span>
                <span>{g.label}{g.required && !g.met ? " — required" : ""}</span>
              </div>
            ))}
            {!gatesMet && (
              <div className="text-[11px] text-slate-500 pt-1">
                Finish these on the deal first. Chad or Robert then approves the build on the Build Approvals board.
              </div>
            )}
          </div>
        )}
        {submitsBuild && (
          <BuildPaymentPlanPicker
            snap={pay.snap}
            loading={pay.loading}
            error={pay.error}
            value={planChoice}
            onChange={setPlanChoice}
            forcePlan={forcePlan}
            compact={compact}
          />
        )}
        {planErr && <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">{planErr}</div>}
        {config.fields.length === 0 ? (
          submitsBuild ? null : <div className="text-[12px] text-slate-500 py-2 text-center">No additional information needed — just confirm.</div>
        ) : (
          fieldStatus.map((f) => (
            <div key={f.key}>
              <div className="flex items-center gap-2 mb-1">
                <div className={
                  "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold " +
                  (f.met ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600")
                }>
                  {f.met ? "✓" : "!"}
                </div>
                <label className="text-[12px] font-medium text-slate-700">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
              </div>
              {f.hint && <div className="ml-7 mb-1 text-[10px] leading-snug text-slate-400">{f.hint}</div>}

              {f.type === "check" ? (
                <div className="text-[11px] text-slate-500 ml-7">
                  {f.met ? "Already completed" : "Not yet — complete this step first"}
                </div>
              ) : f.type === "select" ? (
                <select
                  value={String(formData[f.key] ?? "")}
                  onChange={(e) => updateField(f.key, e.target.value)}
                  className="ml-7 w-[calc(100%-28px)] text-[12px] border border-slate-200 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="">Select...</option>
                  {(f.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : f.type === "datetime" ? (
                <div className="ml-7 flex gap-2">
                  <input
                    type="date"
                    value={String(formData[f.key] ?? "").slice(0, 10)}
                    onChange={(e) => {
                      const time = String(formData[f.key] ?? "").slice(11, 16) || "10:00";
                      updateField(f.key, e.target.value + "T" + time + ":00Z");
                    }}
                    className="flex-1 text-[12px] border border-slate-200 rounded-lg px-3 py-2"
                  />
                  <input
                    type="time"
                    value={String(formData[f.key] ?? "").slice(11, 16)}
                    onChange={(e) => {
                      const date = String(formData[f.key] ?? "").slice(0, 10) || new Date().toISOString().slice(0, 10);
                      updateField(f.key, date + "T" + e.target.value + ":00Z");
                    }}
                    className="w-28 text-[12px] border border-slate-200 rounded-lg px-3 py-2"
                  />
                </div>
              ) : f.type === "upload" ? (
                <div className="ml-7">
                  {uploadFiles[f.key] ? (
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-emerald-600 font-medium truncate">
                        📎 {uploadFiles[f.key].name} — uploads on save
                      </span>
                      <button
                        type="button"
                        className="text-slate-400 hover:text-slate-600 shrink-0"
                        onClick={() => {
                          setUploadFiles((prev) => {
                            const next = { ...prev };
                            delete next[f.key];
                            return next;
                          });
                          updateField(f.key, "");
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : f.met ? (
                    <div className="text-[11px] text-emerald-600 font-medium">Uploaded</div>
                  ) : (
                    <label className="block border-2 border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition">
                      <div className="text-[11px] text-slate-500">Drop file or tap to upload</div>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const picked = e.target.files?.[0];
                          if (!picked) return;
                          setUploadFiles((prev) => ({ ...prev, [f.key]: picked }));
                          updateField(f.key, "true");
                        }}
                      />
                    </label>
                  )}
                </div>
              ) : (
                <>
                  <input
                    type={f.type === "currency" || f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "phone" ? "tel" : "text"}
                    value={String(formData[f.key] ?? "")}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="ml-7 w-[calc(100%-28px)] text-[12px] border border-slate-200 rounded-lg px-3 py-2"
                  />
                  {f.warnFn === "supplement_vs_rcv" && (
                    <div className="ml-7">
                      <SupplementEntryWarning
                        entered={Number(formData[f.key] ?? 0) || 0}
                        rcv={Number((deal as unknown as Record<string, unknown>).rcv_total ?? 0) || 0}
                        onUse={(v) => updateField(f.key, String(v))}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-slate-100 flex gap-2">
        {canAdvance ? (
          <button
            onClick={() => handleSave(true)}
            disabled={saving || !planOk}
            className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
          >
            {saving ? "Saving…" : `Save & advance`}
          </button>
        ) : canOverride ? (
          <>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white text-slate-700 border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 transition disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save partial"}
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Override: advance with ${totalRequired - completedRequired} missing item(s)?`)) {
                  handleSave(true);
                }
              }}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg bg-amber-500 text-white text-[12px] font-semibold hover:bg-amber-600 transition disabled:opacity-50"
            >
              Override
            </button>
          </>
        ) : (
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white text-slate-700 border border-slate-200 text-[12px] font-semibold hover:bg-slate-50 transition disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save partial"}
          </button>
        )}
        <button
          onClick={() => setOpen(false)}
          className="px-3 py-2.5 text-[12px] text-slate-400 hover:text-slate-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
